import { MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import { useImmer } from "use-immer";
import assert from "utils/assert";
import {
  ConversationItem,
  ConversationItemAddedEvent,
  ConversationItemDoneEvent,
  ConversationItemInputAudioTranscriptionDeltaEvent,
  ConversationRole,
  RealtimeResponseFunctionCallItem,
  ResponseDoneEvent,
  ResponseOutputAudioTranscriptDeltaEvent,
  ResponseOutputTextDeltaEvent,
} from "./events";
import {
  END_CONVERSATION_PROMPT,
  EXERCISE_GENERATION_TIMED_OUT,
  getExerciseAnswerPrompt,
  SCENARIOS_PROMPT,
} from "./prompts";
import * as tools from "./tools";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { logError, logInfo, logWarning } from "services/logging";
import { updateTallyPreferences } from "./endpoints";
import { aiExercisesQuery, createAiExercises, pollAiExerciseSequence } from "./exercises";
import createTallyConnection, {
  type EventHandlerMap,
  type TallyConnection,
} from "./TallyConnection";

export type TallyConversation = {
  conversationId: string | undefined;
  status: TallyConversationStatus;
  conversationItems: ConversationItem[];

  startConversation: () => Promise<void>;
  endConversation: () => void;
  closeConversation: () => void;

  audioInputEnabled: boolean;
  audioOutputEnabled: boolean;
  toggleAudio: (enabled: boolean) => void;

  sendChatMessage: (message: string) => void;
  sendExerciseAnswer: (props: ExerciseAnswerSubmission) => void;
};

export type TallyConversationStatus =
  | "not-started"
  | "connecting"
  | "in-progress"
  | "wrapping-up"
  | "ended";

export type ExerciseAnswerSubmission = {
  exerciseId: string;
  answerId: string;
  isCorrect: boolean;
  isLastInSequence: boolean;
};

export function useTallyConversation(initialScenario?: string): TallyConversation {
  const [conversationId, setConversationId] = useState<string>();
  const [conversationStatus, setConversationStatus] =
    useState<TallyConversationStatus>("not-started");
  const [conversationItems, setConversationItems] = useImmer<ConversationItem[]>([]);

  const connectionRef = useRef<TallyConnection>();

  const statusRef = useRef<TallyConversationStatus>("not-started");
  const setStatus = (status: TallyConversationStatus) => {
    statusRef.current = status;
    setConversationStatus(status);
  };

  const createConversationItem = async (
    role: ConversationRole,
    text: string,
    timeout?: number
  ) => {
    const conn = connectionRef.current;
    assert(conn, "No connection for creating conversation item");

    const itemId = `${role}-message-${Date.now()}`;

    conn.sendRealtimeEvent({
      type: "conversation.item.create",
      item: {
        id: itemId,
        type: "message",
        role,
        content: [
          {
            type: "input_text",
            text,
          },
        ],
      },
    });

    await conn.waitForRealtimeEvent(
      "conversation.item.done",
      event => event.item.id === itemId,
      timeout
    );
  };

  const audioController = useAudioController(connectionRef, conversationStatus);
  const exerciseController = useExercises(connectionRef, createConversationItem);

  const startConversation = async () => {
    setStatus("connecting");

    // The map is typed void-returning because the connection never waits on a
    // handler; this one has to run in order, so it is its own function.
    const onSessionCreated = async () => {
      // Mute the mic during Tally's opening line so the echo canceller can converge
      // and they don't hear themselves
      audioController.toggleAudioInput(false);

      if (initialScenario) {
        try {
          await createConversationItem(
            "system",
            `${SCENARIOS_PROMPT}: ${initialScenario}`
          );
        } catch (error) {
          logError("Error creating initial scenario item:", error);
        }
      }

      conn.sendRealtimeEvent({ type: "response.create" });

      try {
        await conn.waitForRealtimeEvent(
          "output_audio_buffer.stopped",
          () => true,
          GREETING_BARGE_IN_DELAY
        );
      } catch {
        // Greeting still going after the delay: open the mic anyway so the
        // user can barge in. Relies on echo cancellation to keep Tally's own
        // voice from tripping the server's turn detection.
      } finally {
        // Only advance if we're still connecting — if the user ended/left
        // during the greeting, don't re-open the mic or resurrect the session.
        if (statusRef.current === "connecting") {
          // Open the mic (toggleAudioInput also clears the input buffer, dropping
          // anything captured during the greeting).
          audioController.toggleAudioInput(true);
          setStatus("in-progress");
        }
      }
    };

    const eventHandlers: EventHandlerMap = {
      "session.created": () => void onSessionCreated(),

      "conversation.item.added": (serverEvent: ConversationItemAddedEvent) => {
        setConversationItems(prevItems => [...prevItems, serverEvent.item]);

        // The model calling end_conversation is the verbal-goodbye end of the session.
        const item = serverEvent.item;
        if (item.type === "function_call" && item.name === tools.names.endConversation) {
          void finalize("model", item.id);
        }
      },

      "conversation.item.done": (serverEvent: ConversationItemDoneEvent) => {
        const itemType = serverEvent.item.type;
        if (itemType === "function_call" || itemType === "function_call_output") {
          setConversationItems(prevItems => {
            const index = prevItems.findIndex(i => i.id === serverEvent.item.id);
            assert(index !== -1, "Completed item not found in conversation items");
            prevItems[index] = serverEvent.item;
          });
        }
      },

      "response.output_text.delta": handleMessageDelta,
      "response.output_audio_transcript.delta": handleMessageDelta,
      "conversation.item.input_audio_transcription.delta": handleMessageDelta,

      "response.done": (serverEvent: ResponseDoneEvent) => {
        const funcCalls = serverEvent.response.output.filter(
          item => item.type === "function_call"
        );

        for (const funcCall of funcCalls) {
          logInfo("Processing function call:", funcCall.name);
          if (funcCall.name === tools.names.createExercises) {
            void exerciseController.onCreateExercises(funcCall);
          } else if (funcCall.name === tools.names.updatePreferences) {
            void onUpdatePreferences(conn, funcCall);
          }
        }
      },
    };

    const onConnectionClosed = () => {
      // Intentional teardowns (End/Close) reach a terminal status before closing
      // the connection, so this only reacts to *unexpected* drops mid-session.
      if (statusRef.current === "connecting" || statusRef.current === "in-progress") {
        setStatus("ended");
        audioController.reset();
      }
    };

    const conn = await createTallyConnection(eventHandlers, onConnectionClosed);
    connectionRef.current = conn;
    setConversationId(conn.conversationId);
  };

  const handleMessageDelta = (serverEvent: MessageDeltaEvent) => {
    setConversationItems(prevItems => {
      const item = prevItems.find(i => i.id === serverEvent.item_id);
      if (item) {
        assert(item.type === "message", "Item is not a message");
        item.content.push({ type: "input_text", text: serverEvent.delta });
      } else {
        logWarning("Item not found for updates:", serverEvent.item_id);
      }
    });
  };

  const sendChatMessage = async (message: string) => {
    await createConversationItem("user", message);

    assert(connectionRef.current, "No connection for sending chat message");
    connectionRef.current.sendRealtimeEvent({
      type: "response.create",
      response: {
        output_modalities: ["text"],
      },
    });
  };

  // Wrap up and tear down the session. Two initiators share this path:
  // - "user": the End button — the model doesn't know yet, so we drive the
  //   goodbye/report ourselves.
  // - "model": the model called end_conversation on a verbal goodbye — it has
  //   already produced the goodbye + report, so we only let the audio play out.
  // Both then wait for the goodbye audio to finish before closing.
  const finalize = async (initiatedBy: "user" | "model", endItemId?: string | null) => {
    const conn = connectionRef.current;
    if (!conn) {
      logError("No connection for ending conversation");
      return;
    }

    // End during the greeting: nothing worth reporting yet — abort to the landing screen.
    if (statusRef.current === "connecting") {
      conn.close();
      resetConversation();
      return;
    }

    if (statusRef.current !== "in-progress") return;

    setStatus("wrapping-up");
    audioController.toggleAudioInput(false);

    try {
      if (initiatedBy === "user") {
        // Cancel any in-flight response so our wrap-up response.create doesn't
        // collide with it ("conversation already has an active response" error).
        conn.sendRealtimeEvent({ type: "response.cancel" });
        conn.sendRealtimeEvent({ type: "output_audio_buffer.clear" });

        const lastItem = conversationItems.at(-1);
        if (lastItem?.id) {
          conn.sendRealtimeEvent({
            type: "conversation.item.truncate",
            item_id: lastItem.id,
            content_index: 0,
            audio_end_ms: 0,
          });
        }

        await createConversationItem("system", END_CONVERSATION_PROMPT);
        conn.sendRealtimeEvent({ type: "response.create" });
        const created = await conn.waitForRealtimeEvent(
          "response.created",
          () => true,
          WRAP_UP_TIMEOUT
        );
        await conn.waitForRealtimeEvent(
          "response.done",
          event => event.response.id === created.response.id,
          WRAP_UP_TIMEOUT
        );
        // Let the goodbye audio finish playing before tearing down.
        await conn.waitForRealtimeEvent(
          "output_audio_buffer.stopped",
          () => true,
          GOODBYE_TIMEOUT
        );
      } else if (endItemId) {
        // Wait for both so short goodbye audio can't drain and close the connection before the report lands.
        await Promise.allSettled([
          conn.waitForRealtimeEvent(
            "conversation.item.done",
            event => event.item.id === endItemId,
            WRAP_UP_TIMEOUT
          ),
          conn.waitForRealtimeEvent(
            "output_audio_buffer.stopped",
            () => true,
            GOODBYE_TIMEOUT
          ),
        ]);
      }
    } catch (error) {
      logWarning("End conversation: wrap-up did not complete in time", error);
    } finally {
      // A newer session may have started while we awaited (e.g. drop + restart);
      // don't stomp its status, but still close *this* session's connection.
      if (connectionRef.current === conn) {
        setStatus("ended");
      }
      conn.close();
    }
  };

  const endConversation = () => finalize("user");

  const resetConversation = () => {
    setStatus("not-started");
    setConversationId(undefined);
    setConversationItems([]);
    audioController.reset();
  };

  // "Close" after the session has ended: tear down the (possibly still-open)
  // connection and return to the landing screen.
  const closeConversation = () => {
    // Reset to not-started before close(), so onConnectionClosed sees it and no-ops.
    resetConversation();
    connectionRef.current?.close();
  };

  useEffect(() => {
    const currentConnection = connectionRef.current;
    if (conversationId && currentConnection) {
      return () => currentConnection.close();
    }
  }, [conversationId]);

  return {
    conversationId,
    status: conversationStatus,
    conversationItems,

    startConversation,
    endConversation: () => void endConversation(),
    closeConversation,

    audioInputEnabled: audioController.inputEnabled,
    audioOutputEnabled: audioController.outputEnabled,
    toggleAudio: audioController.toggleAudio,

    sendChatMessage: (message: string) => void sendChatMessage(message),
    sendExerciseAnswer: exerciseController.sendExerciseAnswer,
  };
}

type MessageDeltaEvent =
  | ResponseOutputAudioTranscriptDeltaEvent
  | ConversationItemInputAudioTranscriptionDeltaEvent
  | ResponseOutputTextDeltaEvent;

export const MIN_CONVERSATION_START_CREDITS = 1000;

// How long to keep the mic muted at the start so the echo canceller can
// converge and Tally's greeting plays clean. The mic opens the moment her
// greeting audio ends, or after this delay — whichever comes first — so the
// user can barge in over a long greeting.
const GREETING_BARGE_IN_DELAY = 5000;

// Max time the End flow waits for the model to produce the goodbye/report.
const WRAP_UP_TIMEOUT = 15000;

// Max time to wait for the (already-generated) goodbye audio to finish playing
// out before tearing down — shorter than WRAP_UP_TIMEOUT since nothing is being
// generated, we're only letting buffered audio drain.
const GOODBYE_TIMEOUT = 8000;

function useAudioController(
  connectionRef: MutableRefObject<TallyConnection | undefined>,
  conversationStatus: TallyConversationStatus
) {
  const [inputEnabled, setInputEnabled] = useState<boolean>(false);
  const [outputEnabled, setOutputEnabled] = useState<boolean>(true);

  const toggleAudioInput = useCallback(
    (enabled: boolean) => {
      const conn = connectionRef.current;
      assert(conn, "No connection for toggling audio input");

      conn.toggleInputTrack(enabled);
      setInputEnabled(enabled);

      conn.sendRealtimeEvent({
        type: "input_audio_buffer.clear",
      });
    },
    [connectionRef]
  );

  const toggleAudioOutput = useCallback(
    (enabled: boolean) => {
      const conn = connectionRef.current;
      assert(conn, "No connection for toggling audio output");
      conn.toggleOutputTrack(enabled);
      setOutputEnabled(enabled);

      conn.sendRealtimeEvent({
        type: "output_audio_buffer.clear",
      });
    },
    [connectionRef]
  );

  const toggleAudio = useCallback(
    (enabled: boolean) => {
      toggleAudioInput(enabled);
      toggleAudioOutput(enabled);
    },
    [toggleAudioInput, toggleAudioOutput]
  );

  useEffect(() => {
    if (conversationStatus !== "in-progress") return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        toggleAudio(false);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [toggleAudio, connectionRef, conversationStatus]);

  const reset = () => {
    setInputEnabled(false);
    setOutputEnabled(true);
  };

  return {
    outputEnabled,
    inputEnabled,
    toggleAudio,
    toggleAudioInput,
    toggleAudioOutput,
    reset,
  };
}

function useExercises(
  connectionRef: MutableRefObject<TallyConnection | undefined>,
  addConversationItem: (role: ConversationRole, text: string) => Promise<void>
) {
  const queryClient = useQueryClient();
  const { mutateAsync: generateExercises } = useMutation({
    mutationFn: createAiExercises,
  });

  const onCreateExercises = async (funcCall: RealtimeResponseFunctionCallItem) => {
    // FIXME don't close connection if exercise are being generated
    const conn = connectionRef.current;
    if (!conn) {
      logError("No connection for creating exercises");
      return;
    }

    // Every exit path must send a function_call_output, or the model waits on the
    // result forever and the chat chip spins indefinitely.
    const closeToolCall = (output: object) =>
      conn.sendRealtimeEvent({
        type: "conversation.item.create",
        item: {
          type: "function_call_output",
          call_id: funcCall.call_id,
          output: JSON.stringify(output),
        },
      });

    const fail = (error: unknown) => {
      logError("Error generating exercises:", error);
      closeToolCall({ error: "Exercise generation failed" });
      void addConversationItem("system", EXERCISE_GENERATION_TIMED_OUT);
    };

    const args = tools.parseFunctionArguments<tools.CreateExercisesArgs>(
      funcCall.arguments
    );
    if (!args) {
      fail("Failed to parse create_exercises arguments");
      return;
    }

    try {
      const response = await generateExercises({
        context: args.context,
        tally_conversation_id: conn.conversationId,
      });
      const exerciseSequence = await pollAiExerciseSequence(response.id);
      queryClient.setQueryData(
        aiExercisesQuery({ id: exerciseSequence.id }).queryKey,
        exerciseSequence
      );
      closeToolCall({
        type: tools.names.createExercises,
        exercise_sequence: exerciseSequence,
      } satisfies tools.CreateExercisesOutput);
    } catch (error) {
      fail(error);
    }
  };

  const sendExerciseAnswer = (props: ExerciseAnswerSubmission) => {
    void addConversationItem("system", getExerciseAnswerPrompt(props));
  };

  return {
    sendExerciseAnswer,
    onCreateExercises,
  };
}

async function onUpdatePreferences(
  conn: TallyConnection,
  funcCall: RealtimeResponseFunctionCallItem
) {
  assert(conn, "No connection for updating preferences");
  const args = tools.parseFunctionArguments<tools.UpdatePreferencesArgs>(
    funcCall.arguments
  );

  // Every exit path must close the tool call (or the model waits on the result
  // forever) and then prompt a spoken response: the tool description tells Tally
  // to acknowledge on {ok:true} and to say she couldn't make the change on
  // {ok:false}, so we always give her the turn and let her word it.
  const closeToolCall = (ok: boolean) => {
    conn.sendRealtimeEvent({
      type: "conversation.item.create",
      item: {
        type: "function_call_output",
        call_id: funcCall.call_id,
        output: JSON.stringify({ ok }),
      },
    });

    conn.sendRealtimeEvent({ type: "response.create" });
  };

  if (!args) {
    logError(
      "Failed to parse update_conversation_preferences arguments",
      funcCall.arguments
    );
    closeToolCall(false);
    return;
  }

  try {
    const resolved = await updateTallyPreferences(args);

    conn.sendRealtimeEvent({
      type: "session.update",
      session: {
        type: "realtime",
        audio: {
          input: { turn_detection: resolved.turn_detection },
          output: { speed: resolved.output_speed },
        },
      },
    });

    closeToolCall(true);
  } catch (error) {
    logError("Error updating tally preferences:", error);
    closeToolCall(false);
  }
}
